import React, { useEffect, useState } from "react";
import { useLocation, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { PackageSearch, Loader2, Check } from "lucide-react";
import api, { inr, formatApiErrorDetail } from "../lib/api";

const FLOW = ["Placed", "Confirmed", "Shipped", "Out for Delivery", "Delivered"];

function Timeline({ order }) {
  const cancelled = order.status === "Cancelled";
  const currentIdx = FLOW.indexOf(order.status);
  return (
    <div className="mt-2">
      {cancelled ? (
        <div className="bg-destructive/10 text-destructive px-4 py-3 text-sm font-semibold">
          This order was cancelled.
        </div>
      ) : (
        <div className="flex flex-col sm:flex-row sm:items-start">
          {FLOW.map((step, i) => {
            const done = i <= currentIdx;
            const active = i === currentIdx;
            return (
              <div key={step} className="flex sm:flex-col sm:flex-1 items-center gap-3 sm:gap-2 sm:text-center">
                <div className="flex sm:flex-col items-center sm:w-full">
                  <div
                    className={`h-8 w-8 rounded-full flex items-center justify-center shrink-0 border-2 ${
                      done ? "bg-primary border-primary text-primary-foreground" : "border-border text-muted-foreground"
                    } ${active ? "ring-4 ring-primary/15" : ""}`}
                  >
                    {done ? <Check size={16} /> : <span className="text-xs">{i + 1}</span>}
                  </div>
                  {i < FLOW.length - 1 && (
                    <div className={`hidden sm:block h-0.5 flex-1 w-full ${i < currentIdx ? "bg-primary" : "bg-border"}`} style={{ position: "relative", top: "-16px", left: "50%", width: "100%" }} />
                  )}
                </div>
                <span className={`text-xs sm:mt-1 ${done ? "text-primary font-semibold" : "text-muted-foreground"}`}>
                  {step}
                </span>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

export default function TrackOrder() {
  const { state } = useLocation();
  const [sp] = useSearchParams();
  const [orderNumber, setOrderNumber] = useState(sp.get("order") || state?.order_number || "");
  const [contact, setContact] = useState(sp.get("contact") || "");
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(false);

  const doTrack = async (on, ct) => {
    if (!on.trim() || !ct.trim()) {
      toast.error("Enter your Order ID and email or phone");
      return;
    }
    setLoading(true);
    setOrder(null);
    try {
      const { data } = await api.get("/orders/track", {
        params: { order_number: on, contact: ct },
      });
      setOrder(data);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  // Auto-track when arriving from an email link (?order=...&contact=...)
  useEffect(() => {
    const on = sp.get("order");
    const ct = sp.get("contact");
    if (on && ct) doTrack(on, ct);
    // eslint-disable-next-line
  }, []);

  const track = (e) => {
    e?.preventDefault();
    doTrack(orderNumber, contact);
  };

  return (
    <div className="max-w-3xl mx-auto px-5 sm:px-8 py-14" data-testid="track-page">
      <div className="text-center">
        <PackageSearch size={44} className="mx-auto text-primary" strokeWidth={1.3} />
        <h1 className="mt-5 text-4xl text-primary font-medium">Track your order</h1>
        <p className="mt-3 text-muted-foreground">
          Enter your Order ID with the email or phone used at checkout.
        </p>
      </div>

      <form onSubmit={track} className="mt-8 grid grid-cols-1 sm:grid-cols-[1fr_1fr_auto] gap-3">
        <input
          value={orderNumber}
          onChange={(e) => setOrderNumber(e.target.value)}
          placeholder="Order ID (e.g. AD2604…)"
          className="bg-muted px-4 py-3 text-sm outline-none focus:ring-1 focus:ring-primary"
          data-testid="track-order-number"
        />
        <input
          value={contact}
          onChange={(e) => setContact(e.target.value)}
          placeholder="Email or phone"
          className="bg-muted px-4 py-3 text-sm outline-none focus:ring-1 focus:ring-primary"
          data-testid="track-contact"
        />
        <button
          type="submit"
          disabled={loading}
          className="bg-primary text-primary-foreground px-8 py-3 text-sm flex items-center justify-center gap-2 disabled:opacity-60"
          data-testid="track-submit"
        >
          {loading && <Loader2 size={16} className="animate-spin" />} Track
        </button>
      </form>

      {order && (
        <div className="mt-10 border border-border" data-testid="track-result">
          <div className="bg-secondary/50 px-6 py-4 flex flex-wrap items-center justify-between gap-3 border-b border-border">
            <div>
              <p className="text-xs uppercase tracking-[0.15em] text-muted-foreground">Order</p>
              <p className="font-display text-lg text-primary">{order.order_number}</p>
            </div>
            <span className="text-sm px-3 py-1 bg-primary text-primary-foreground">{order.status}</span>
          </div>
          <div className="px-6 py-8">
            <Timeline order={order} />
          </div>
          <div className="px-6 pb-6 space-y-3">
            {order.items.map((i, idx) => (
              <div key={idx} className="flex items-center gap-3">
                <div className="w-12 aspect-[4/5] bg-muted overflow-hidden shrink-0">
                  <img src={i.image} alt={i.name} className="w-full h-full object-cover" />
                </div>
                <div className="flex-1">
                  <p className="text-sm">{i.name}</p>
                  <p className="text-xs text-muted-foreground">Qty {i.qty}</p>
                </div>
                <span className="text-sm font-semibold">{inr(i.price * i.qty)}</span>
              </div>
            ))}
            <div className="border-t border-border pt-3 flex justify-between">
              <span className="font-semibold">Total</span>
              <span className="font-semibold text-primary">{inr(order.total)}</span>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
